import React from 'react';
import { CheckCircle2, XCircle, Clock, DollarSign, ExternalLink, Star } from 'lucide-react';

interface AgentResponse {
  step_id: number;
  agent_id: string;
  agent_name: string;
  capability: string;
  response_text: string;
  response_json?: any;
  success: boolean;
  response_time_ms: number;
  cost_usdc: number;
  quality_assessment?: {
    overall_score: number;
    accuracy_score: number;
    relevance_score: number;
    completeness_score: number;
    helpfulness_score: number;
    assessment_reasoning: string;
  };
}

interface ExecutionResult {
  query_id: string;
  plan_id: string;
  query: string;
  status: string;
  total_cost_usdc: number;
  total_duration_ms: number;
  responses: AgentResponse[];
}

interface ExecutionResultsDisplayProps {
  result: ExecutionResult;
  onClose: () => void;
  onNewQuery: () => void;
}

export function ExecutionResultsDisplay({
  result,
  onClose,
  onNewQuery,
}: ExecutionResultsDisplayProps) {
  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  };

  const formatCost = (cost: number) => {
    return `$${cost.toFixed(4)} USDC`;
  };

  const getQualityColor = (score: number) => {
    if (score >= 0.9) return 'text-green-600';
    if (score >= 0.7) return 'text-blue-600';
    if (score >= 0.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getQualityBg = (score: number) => {
    if (score >= 0.9) return 'bg-green-100';
    if (score >= 0.7) return 'bg-blue-100';
    if (score >= 0.5) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-semibold text-gray-900">Execution Results</h2>
              {result.status === 'completed' ? (
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              ) : (
                <XCircle className="w-6 h-6 text-red-600" />
              )}
            </div>
            <p className="text-sm text-gray-600 mb-4">{result?.query || 'No query'}</p>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="font-mono">Plan: {result?.plan_id?.substring(0, 8) || 'N/A'}...</span>
              <span className="font-mono">Query: {result?.query_id?.substring(0, 8) || 'N/A'}...</span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-600 mb-1">
            <DollarSign className="w-4 h-4" />
            <span className="text-sm">Total Cost</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {formatCost(result?.total_cost_usdc || 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-600 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Total Time</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {formatDuration(result?.total_duration_ms || 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-gray-600 mb-1">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-sm">Success Rate</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {result?.responses?.length ? ((result.responses.filter((r) => r.success).length / result.responses.length) * 100).toFixed(0) : 0}%
          </p>
        </div>
      </div>

      {/* Agent Responses */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Agent Responses</h3>
        {(result?.responses || []).map((response, index) => (
          <div
            key={response.step_id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            {/* Response Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center font-semibold text-sm">
                  {index + 1}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-900">{response.agent_name}</h4>
                    {response.success ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {response.capability} • {response.agent_id}
                  </p>
                </div>
              </div>
              {response.quality_assessment && (
                <div className="text-right">
                  <div className="flex items-center gap-1 justify-end mb-1">
                    <Star className={`w-4 h-4 ${getQualityColor(response.quality_assessment.overall_score)}`} />
                    <span className={`text-lg font-bold ${getQualityColor(response.quality_assessment.overall_score)}`}>
                      {(response.quality_assessment.overall_score * 10).toFixed(1)}
                    </span>
                    <span className="text-sm text-gray-500">/10</span>
                  </div>
                  <p className="text-xs text-gray-500">Quality Score</p>
                </div>
              )}
            </div>

            {/* Response Content */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{response.response_text}</p>
            </div>

            {/* Quality Breakdown */}
            {response.quality_assessment && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Quality Breakdown</p>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: 'Accuracy', score: response.quality_assessment.accuracy_score },
                    { label: 'Relevance', score: response.quality_assessment.relevance_score },
                    { label: 'Complete', score: response.quality_assessment.completeness_score },
                    { label: 'Helpful', score: response.quality_assessment.helpfulness_score },
                  ].map((metric) => (
                    <div key={metric.label}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-600">{metric.label}</span>
                        <span className="text-xs font-semibold text-gray-900">
                          {(metric.score * 10).toFixed(1)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${getQualityBg(metric.score)}`}
                          style={{ width: `${metric.score * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {response.quality_assessment.assessment_reasoning && (
                  <p className="text-xs text-gray-600 mt-3 italic">
                    {response.quality_assessment.assessment_reasoning}
                  </p>
                )}
              </div>
            )}

            {/* Response Metadata */}
            <div className="flex items-center gap-6 text-sm text-gray-600 pt-3 border-t border-gray-200">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{formatDuration(response.response_time_ms)}</span>
              </div>
              <div className="flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                <span>{formatCost(response.cost_usdc)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={onClose}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
        >
          Close
        </button>
        <button
          onClick={onNewQuery}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          New Query
        </button>
      </div>
    </div>
  );
}
